import React, { useEffect, useMemo, useState } from "react";
import { CalendarClock, Trash2, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { PageHeader, Section, EmptyState, LoadingBlock } from "../../components/app/ui";
import PlaceInput from "../../components/app/PlaceInput";
import { SmartTag, Chips } from "../../components/app/SmartDefaults";
import { nextSlot, toLocalInput, scheduleChips } from "../../lib/smartDefaults";
import { useAuth } from "../../context/AuthContext";
import { ridesApi, inr, formatDate, errorMessage, titleCase } from "../../lib/api";

const VEHICLES = [{ value: "scooter", label: "Scooter" }, { value: "auto", label: "Autorickshaw" }, { value: "car", label: "Taxi Car" }, { value: "jumbo", label: "Jumbo Taxi" }];

const defaultTime = () => toLocalInput(nextSlot());

export default function Schedule() {
  const { user } = useAuth();
  const isDriver = user?.role === "driver";
  const [items, setItems] = useState(null);
  const [pickup, setPickup] = useState(null);
  const [drop, setDrop] = useState(null);
  const [when, setWhen] = useState(defaultTime);
  const [whenAuto, setWhenAuto] = useState(true);
  const chips = useMemo(() => [...scheduleChips(), { id: "custom", label: "Pick a date…", value: null }], []);
  const activeChip = chips.find((c) => c.value === when)?.id || "custom";
  const pickChip = (c) => {
    setWhenAuto(false);
    if (c.value) setWhen(c.value);
    else { const el = document.querySelector('[data-testid="sched-time"]'); el?.focus(); el?.showPicker?.(); }
  };
  const [vehicle, setVehicle] = useState("car");
  const [notes, setNotes] = useState("");
  const [estimate, setEstimate] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => ridesApi.scheduled().then((s) => setItems([...s].sort((a, b) => (a.scheduled_at || "").localeCompare(b.scheduled_at || "")))).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    setEstimate(null);
    if (!pickup || !drop) return;
    ridesApi.estimate({ pickup_lat: pickup.lat, pickup_lng: pickup.lng, drop_lat: drop.lat, drop_lng: drop.lng }).then(setEstimate).catch(() => {});
  }, [pickup, drop]);
  const fare = estimate?.options?.find((o) => o.vehicle_type === vehicle)?.fare;

  const create = async (e) => {
    e.preventDefault();
    if (!pickup || !drop) return toast.error("Choose pickup and drop");
    if (new Date(when).getTime() < Date.now() + 15 * 60000) return toast.error("Pick a time at least 15 minutes from now");
    setBusy(true);
    try {
      await ridesApi.schedule({ pickup_lat: pickup.lat, pickup_lng: pickup.lng, drop_lat: drop.lat, drop_lng: drop.lng, pickup_address: pickup.name, drop_address: drop.name, vehicle_type: vehicle, scheduled_at: new Date(when).toISOString(), notes: notes.trim() || null });
      toast.success("Ride scheduled");
      setPickup(null); setDrop(null); setNotes(""); load();
    } catch (err) { toast.error(errorMessage(err, "Could not schedule")); } finally { setBusy(false); }
  };
  const remove = async (s) => { try { await ridesApi.deleteScheduled(s.schedule_id || s.id); toast.success("Schedule removed"); load(); } catch (err) { toast.error(errorMessage(err)); } };

  return (
    <>
      <PageHeader title="Schedule" description={isDriver ? "Your upcoming scheduled trips." : "Book a ride ahead of time. A driver will be assigned before pickup."} />
      <div className="grid gap-6 lg:grid-cols-[1fr_420px]">
        <div>
          {items === null && <LoadingBlock rows={3} />}
          {items?.length === 0 && <EmptyState icon={CalendarClock} title="Nothing scheduled" text="Scheduled rides will appear here with their pickup time." />}
          <ul className="space-y-3" data-testid="scheduled-list">
            {items?.map((s) => (
              <li key={s.schedule_id || s.id} className="kt-card flex flex-wrap items-center gap-4 p-4" data-testid={`schedule-${s.schedule_id || s.id}`}>
                <span className="flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-xl bg-brand-50 text-brand"><span className="text-[10px] font-semibold uppercase">{new Date(s.scheduled_at).toLocaleString("en-IN", { month: "short" })}</span><span className="text-lg font-bold leading-none">{new Date(s.scheduled_at).getDate()}</span></span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-ink">{formatDate(s.scheduled_at)}</p>
                  <p className="truncate text-xs text-ink-soft">{s.pickup_address} → {s.drop_address}</p>
                  <p className="mt-0.5 text-xs text-ink-muted">{titleCase(s.vehicle_type)}{s.fare ? ` · ${inr(s.fare)}` : ""}{s.status ? ` · ${titleCase(s.status)}` : ""}</p>
                </div>
                <button onClick={() => remove(s)} className="rounded-lg p-2 text-ink-muted hover:bg-red-50 hover:text-red-700" aria-label="Remove" data-testid={`schedule-delete-${s.schedule_id || s.id}`}><Trash2 className="h-4 w-4" /></button>
              </li>
            ))}
          </ul>
        </div>
        <Section title="Schedule a ride">
          <form onSubmit={create} className="space-y-4" data-testid="schedule-form">
            <PlaceInput label="Pickup" value={pickup} onChange={setPickup} placeholder="Pickup place" allowCurrent testId="sched-pickup" />
            <PlaceInput label="Drop" value={drop} onChange={setDrop} placeholder="Destination" testId="sched-drop" />
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2"><Label className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Pickup time</Label>{whenAuto && <SmartTag testId="sched-time-smart" />}</div>
              <Chips options={chips} value={activeChip} onChange={pickChip} testId="sched-time-chips" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5"><Input type="datetime-local" value={when} min={toLocalInput(nextSlot(15))} onChange={(e) => { setWhen(e.target.value); setWhenAuto(false); }} className="h-11 bg-white" data-testid="sched-time" /></div>
              <div className="space-y-1.5"><Label className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Vehicle</Label><Select value={vehicle} onValueChange={setVehicle}><SelectTrigger className="h-11 bg-white" data-testid="sched-vehicle"><SelectValue /></SelectTrigger><SelectContent>{VEHICLES.map((v) => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes for the driver (optional)" className="bg-white" data-testid="sched-notes" />
            {fare != null && <p className="text-sm text-ink-soft">Estimated fare: <span className="font-semibold text-ink">{inr(fare)}</span> · {estimate.distance_km} km</p>}
            <Button type="submit" disabled={busy} className="h-11 w-full rounded-full bg-brand text-white hover:bg-brand-600" data-testid="sched-submit">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />} Schedule ride</Button>
          </form>
        </Section>
      </div>
    </>
  );
}
